import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const HERO_IMAGE = "https://images.unsplash.com/photo-1715688730347-7f951e867601?w=1600&q=75&fm=webp";
const TEAM_IMAGE = "https://images.unsplash.com/photo-1664996551067-cd740c24cfca?w=800&q=75&fm=webp";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function About() {
  return (
    <main className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Team on site visit"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-forest/70" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-sm tracking-[0.3em] uppercase mb-6 text-white/80"
          >
            Our Story
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight"
            data-testid="about-headline"
          >
            About F.E.R.A.Lways
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-cream" data-testid="about-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center mb-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-forest mb-8 leading-tight">
                Ecological Landscape Design<br />
                <span className="italic">Based in Sakleshpura</span>
              </h2>
              <div className="space-y-6 text-foreground/80 leading-relaxed">
                <p>
                  F.E.R.A.Lways is an ecological landscape design practice based in Sakleshpura, Karnataka.
                </p>
                <p>
                  Our work sits at the intersection of landscape design, regenerative agriculture, and 
                  ecosystem restoration. We collaborate with farmers, landowners, and communities who want 
                  to restore the health and resilience of their land.
                </p>
                <p>
                  The name F.E.R.A.Lways reflects our belief that wild ecosystems provide the most powerful 
                  models for land stewardship. <strong>Forest Ecology Regenerative Agriculture and Livelihoods</strong>, 
                  this is the foundation of everything we do.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={TEAM_IMAGE}
                  alt="Estate landscape view"
                  className="w-full h-[400px] lg:h-[500px] object-cover img-hover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-earth/10 rounded-full -z-10" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-forest/10 rounded-full -z-10" />
            </motion.div>
          </div>

          {/* Values Section */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center mb-24"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-12">
              Our Approach
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-8 bg-sand rounded-2xl">
                <h3 className="font-serif text-xl text-forest mb-4">Observe</h3>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  We begin by understanding how your land naturally functions, its water patterns, 
                  soil, vegetation, and ecological relationships.
                </p>
              </div>
              <div className="p-8 bg-sand rounded-2xl">
                <h3 className="font-serif text-xl text-forest mb-4">Design</h3>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  We create systems that work with these natural patterns, not against them, 
                  to build long term ecological health.
                </p>
              </div>
              <div className="p-8 bg-sand rounded-2xl">
                <h3 className="font-serif text-xl text-forest mb-4">Support</h3>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  We guide implementation and provide ongoing stewardship support as your 
                  landscape evolves and matures.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Location Section */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-forest text-white rounded-3xl p-12 lg:p-16"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl mb-6">
                  Rooted in the<br />
                  <span className="italic">Western Ghats</span>
                </h2>
                <p className="text-white/80 leading-relaxed mb-6">
                  Based in Sakleshpura, we work primarily across Karnataka, Kerala, and Tamil Nadu, 
                  the biodiverse landscapes of the Western Ghats and surrounding regions.
                </p>
                <p className="text-white/80 leading-relaxed">
                  Our deep familiarity with these ecosystems, their monsoon patterns, soil types, 
                  native species, and traditional land practices, informs every design we create.
                </p>
              </div>
              <div className="text-center lg:text-right">
                <p className="text-white/60 text-sm tracking-wider uppercase mb-2">Contact Us</p>
                <p className="text-2xl font-serif mb-2">Sakleshpura, Karnataka</p>
                <p className="text-white/70">India</p>
                <div className="mt-6">
                  <a href="tel:+919606591592" className="text-white/80 hover:text-white block">
                    +91 9606591592
                  </a>
                  <a href="mailto:reachout@feralways.org" className="text-white/80 hover:text-white block">
                    reachout@feralways.org
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-sand">
        <div className="container-custom text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-8 max-w-2xl mx-auto">
              Ready to Work Together?
            </h2>
            <p className="text-foreground/70 max-w-xl mx-auto mb-12 leading-relaxed">
              We'd love to learn about your land and explore how ecological design can help.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-forest text-white hover:bg-forest/90 rounded-full px-10 py-5 font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                data-testid="about-contact-btn"
              >
                Get in Touch <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
